import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { UpgradeComponent } from './auth/upgrade/upgrade.component';
import { AuthLoginGuard } from './core/guards/auth-login.guard';
import { AuthResultGuard } from './core/guards/auth-result.guard';
import { OutreachGuard } from './core/guards/outreach.guard';
import { TrialExpiredComponent } from './shared/components/trial-expired/trial-expired.component';
import { ModalNavigationEnum } from './shared/constants';
import { SuccessCenterGuard } from './core/guards/success-center.guard';
import { LaunchDarklyActivateGuard } from './core/guards';
import { FeatureFlagsEnum } from './shared/enums';

const routes: Routes = [
    {
        path: '',
        children: [
            {
                path: '',
                redirectTo: '/account/login',
                pathMatch: 'full'
            },
            {
                path: 'design-system/material-icons',
                canActivate: [AuthResultGuard],
                loadComponent: () =>
                    import('./design-system/pages/material-icons-inventory/material-icons-inventory-page.component')
                        .then(m => m.MaterialIconsInventoryPageComponent),
                data: { title: 'Design System — Material icons' }
            },
            {
                path: 'home',
                canActivate: [AuthLoginGuard],
                loadChildren: () => import('./home/home.module').then(m => m.HomeModule),
            },
            {
                path: 'results',
                canActivate: [AuthResultGuard],
                loadChildren: () => import('./results/results.module').then(m => m.ResultsModule)
            },
            {
                path: 'company/:id',
                canActivate: [AuthResultGuard],
                loadChildren: () => import('./company-detail/company-detail.module').then(m => m.CompaniesDetailModule),
                data: { title: 'Company Details' }
            },
            {
                path: 'company/:id/:isInternational',
                canActivate: [AuthResultGuard],
                loadChildren: () => import('./company-detail/company-detail.module').then(m => m.CompaniesDetailModule),
                data: { title: 'Company Details' }
            },
            {
                path: 'upgrade',
                component: UpgradeComponent,
            },
            {
                path: 'trial-expired',
                component: TrialExpiredComponent
            },
            {
                path: 'outreach',
                canActivate: [OutreachGuard],
                loadChildren: () => import('./oauth/oauth.module').then(m => m.OauthModule),
            },

            // MODALS
            {
                path: ModalNavigationEnum.ACCOUNT_SETTINGS,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/account-settings/account-settings.module').then(m => m.AccountSettingsModule),
            },
            {
                path: ModalNavigationEnum.INTENT_SETTINGS,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/intent-settings/intent-settings.module').then(m => m.IntentSettingsModule),
            },
            {
                path: ModalNavigationEnum.AUTOMATION_SETTINGS,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/automation-settings/automation-settings.module').then(m => m.AutomationSettingsModule),
            },
            {
                path: ModalNavigationEnum.COMPANY_NEWS_ALERT_SETUP,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/company-news-alert-setup/company-news-alert-setup.module').then(m => m.CompanyNewsAlertSetupModule),
            },
            {
                path: ModalNavigationEnum.FILE_CENTER,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/file-center/file-center.module').then(m => m.FileCenterModule),
            },
            {
                path: ModalNavigationEnum.GROWTH_EXPORT_ERRORS,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/growth-export-errors/growth-export-errors.module').then(m => m.GrowthExportErrorsModule),
            },
            {
                path: ModalNavigationEnum.NEW_TECHNOLOGY_EXPORT_ERRORS,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/tech-change-export-errors/tech-change-export-errors.module').then(m => m.TechChangeExportErrorsModule),
            },
            {
                path: ModalNavigationEnum.SUCCESS_CENTER,
                canActivate: [SuccessCenterGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/success-center/success-center.module').then(m => m.SuccessCenterModule),
            },
            {
                path: ModalNavigationEnum.DYNAMICS,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/integration-settings/dynamics/dynamics-modal.routes').then(m => m.ROUTES),
            },
            {
                path: ModalNavigationEnum.HUBSPOT,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/integration-settings/hubspot/hubspot-modal.routes').then(m => m.ROUTES),
            },
            {
                path: ModalNavigationEnum.MARKETO,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/integration-settings/marketo/marketo-modal.routes').then(m => m.ROUTES),
            },
            {
                path: ModalNavigationEnum.OUTREACH,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/integration-settings/outreach/outreach-modal.routes').then(m => m.ROUTES),
            },
            {
                path: ModalNavigationEnum.SALESLOFT,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/integration-settings/salesloft/salesloft-modal.routes').then(m => m.ROUTES),
            },
            {
                path: ModalNavigationEnum.SALESFORCE,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/integration-settings/salesforce/salesforce-modal.routes').then(m => m.ROUTES),
            },
            {
                path: ModalNavigationEnum.ZOHO,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/integration-settings/zoho/zoho-modal.routes').then(m => m.ROUTES),
            },
            {
                path: ModalNavigationEnum.TECHNOGRAPHICS,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/integration-settings/technographics/technographics-modal.module').then(m => m.TechnographicsModalModule),
            },
            {
                path: ModalNavigationEnum.MANAGE_TAGS,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/manage-tags/manage-tags.module').then(m => m.ManageTagsModule),
            },
            {
                path: ModalNavigationEnum.VISITOR_INTEL_AUTOMATIONS,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/visitor-intel-automations/visitor-intel-automations.module').then(m => m.VisitorIntelAutomationsModule),
            },
            {
                path: ModalNavigationEnum.ICP_AUTOMATIONS,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/icp-automations/icp-automations.module').then(m => m.IcpAutomationsModule),
            },
            {
                path: ModalNavigationEnum.ICP_INTEL,
                canActivate: [AuthResultGuard, LaunchDarklyActivateGuard],
                outlet: 'modal',
                data: { featureFlag : FeatureFlagsEnum.ICPIntelV2Model },
                loadChildren: () => import('./modals/icp-intel/icp-intel-modal.routes').then(m => m.ROUTES),
            },
            {
                path: ModalNavigationEnum.JOB_CHANGE_AUTOMATIONS,
                canActivate: [AuthResultGuard],
                outlet: 'modal',
                loadChildren: () => import('./modals/job-change-automations/job-change-automations.module').then(m => m.JobChangeAutomationsModule),
            }
        ]
    },
    {
        path: 'account',
        loadChildren: () => import('./auth/auth.module').then(m => m.AuthModule)
    },
    {
        path: '**',
        redirectTo: '/account/login',
        pathMatch: 'full'
    }
];
@NgModule({
    imports: [RouterModule.forRoot(routes, { bindToComponentInputs: true })],
    exports: [RouterModule]
})
export class AppRoutingModule { }
